import React, { useCallback, useEffect, useState } from "react";
import { Plus, Lock, Pencil, History } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fmtMoney, OFFICE_GROUPS } from "../../lib/salary";
import EmployeeDialog from "./EmployeeDialog.jsx";
import RateHistoryDialog from "./RateHistoryDialog.jsx";

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | employee
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/salary/employees");
      setEmployees(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (e) => {
    try {
      await api.put(`/salary/employees/${e.id}`, { active: !e.active });
      toast.success(e.active ? `${e.name} deactivated` : `${e.name} activated`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const groupLabel = (g) => OFFICE_GROUPS.find((o) => o.value === g)?.label || g;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="section-label">Employee Compensation Master</div>
        <button type="button" onClick={() => setEditing("new")} data-testid="employee-add-btn"
          className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold">
          <Plus size={16} /> Add Employee
        </button>
      </div>
      <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
        {loading ? <div className="p-6 text-slate-400 text-sm">Loading…</div> : (
          <table className="w-full min-w-[860px]" data-testid="employee-table">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase bg-slate-50/70">
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Office / Group</th>
                <th className="px-4 py-3 text-right">Monthly Salary</th>
                <th className="px-4 py-3">Effective From</th>
                <th className="px-4 py-3">Quarterly</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id} className={`border-t border-slate-100 ${!e.active ? "opacity-60" : ""}`} data-testid={`employee-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                      {e.name}{e.office_group === "China Office" && <Lock size={12} className="text-slate-400" />}
                    </div>
                    {e.notes && <div className="text-xs text-slate-500">{e.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700">{groupLabel(e.office_group)}</td>
                  <td className="px-4 py-3 text-sm text-right tabular font-semibold text-slate-900">
                    {e.current_rate ? `${e.current_rate.currency} ${fmtMoney(e.current_rate.monthly_salary)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 tabular">{e.current_rate?.effective_from || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {e.quarterly_amount ? `${e.quarterly_currency} ${fmtMoney(e.quarterly_amount)}` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${e.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`employee-status-${e.id}`}>
                      {e.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => setHistory(e)} title="Salary history" data-testid={`employee-history-${e.id}`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-[#0055FF] mr-3">
                      <History size={14} /> {e.rates.length} rate{e.rates.length > 1 ? "s" : ""}
                    </button>
                    <button type="button" onClick={() => setEditing(e)} data-testid={`employee-edit-${e.id}`} className="text-slate-500 hover:text-[#0055FF] mr-3"><Pencil size={15} /></button>
                    <button type="button" onClick={() => toggleActive(e)} data-testid={`employee-toggle-${e.id}`} className="text-xs font-semibold text-slate-600 hover:text-slate-900">
                      {e.active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <EmployeeDialog employee={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {history && <RateHistoryDialog employee={history} onClose={() => setHistory(null)} onChanged={load} />}
    </div>
  );
}
